"use client";

import { useRef, useState } from "react";
import { Bug, Paperclip, Send, Check, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type Status = "idle" | "submitting" | "done";

export default function BugReportForm() {
  const { toast } = useToast();
  const [message, setMessage] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const hasContent = message.trim().length > 0 || fileName !== null;

  const onFileChange = () => {
    const f = fileRef.current?.files?.[0] ?? null;
    setFileName(f ? f.name : null);
    setError(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasContent || status === "submitting") return;
    setStatus("submitting");
    setError(null);

    try {
      const fd = new FormData();
      fd.append("message", message.trim());
      fd.append("page", window.location.href);
      const f = fileRef.current?.files?.[0];
      if (f) fd.append("file", f);

      const res = await fetch("/api/bug-report", { method: "POST", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data?.error || "Failed to submit. Please try again.");
      }

      setStatus("done");
      toast({ title: "Report submitted", description: "Thanks for helping us improve." });
      setMessage("");
      setFileName(null);
      if (fileRef.current) fileRef.current.value = "";
      setTimeout(() => setStatus("idle"), 3000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to submit.";
      setError(msg);
      setStatus("idle");
      toast({ title: "Submission failed", description: msg, variant: "destructive" });
    }
  };

  return (
    <section
      aria-label="Report a bug"
      className="w-full max-w-sm md:max-w-3xl lg:max-w-4xl mx-auto mt-8 animate-pop-in"
      style={{ animationDelay: "950ms" }}
    >
      <div className="bg-[#0a0a0c] border border-zinc-800/80 rounded-3xl theme-card-glow p-5 sm:p-6 text-left">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-2xl theme-accent-border-soft border bg-zinc-950 flex items-center justify-center shrink-0">
            <Bug className="w-5 h-5 theme-accent-text" />
          </div>
          <div>
            <h2 className="text-white font-serif-premium font-bold text-base sm:text-lg leading-tight">
              Report a Bug
            </h2>
            <p className="text-zinc-500 text-xs mt-0.5">
              Found something broken? Tell us — optionally attach a screenshot.
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-3">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            maxLength={2000}
            placeholder="Describe the bug you encountered..."
            aria-label="Bug description"
            className="w-full rounded-xl bg-zinc-900/60 border border-zinc-800/80 px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-zinc-600 theme-ring transition-colors resize-none"
          />

          <input
            ref={fileRef}
            type="file"
            accept="image/*,.pdf,.doc,.docx,.txt,.zip"
            className="hidden"
            aria-label="Attach a file"
            onChange={onFileChange}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-full flex items-center gap-2.5 rounded-xl border border-zinc-800/80 bg-zinc-900/40 px-4 py-3 text-xs text-zinc-500 hover:text-zinc-300 hover:border-zinc-700 transition-colors text-left"
          >
            <Paperclip className="w-4 h-4 shrink-0" />
            <span className="flex-1 min-w-0 truncate">
              {fileName ?? "Attach a screenshot or file (optional, max 8 MB)"}
            </span>
          </button>

          {error && (
            <p role="alert" className="text-red-400 text-xs">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={!hasContent || status !== "idle"}
            className={`w-full py-3 rounded-xl font-bold text-[13px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${
              status === "done"
                ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300"
                : hasContent && status === "idle"
                  ? "theme-btn"
                  : "bg-zinc-900 border border-zinc-800 text-zinc-600 cursor-not-allowed"
            }`}
          >
            {status === "submitting" ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Submitting...
              </>
            ) : status === "done" ? (
              <>
                <Check className="w-4 h-4" /> Submitted Successfully
              </>
            ) : (
              <>
                <Send className="w-4 h-4" /> Submit Report
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}
